import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Dropdown, Input, message, Modal, Select, Skeleton, Tooltip, Upload } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileOutlined,
  MoreOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import APIService from "../../Service/APIService";
import Text from "../../common/typography/text";
import { colors } from "../../theme/colors.js";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import DistributorAccount from "./DistributorAccount";
import DistributorUser from "./DistributorUser";
import "./DistributorCss.css";

const MAX_DISTRIBUTOR_FILE_BYTES = 4 * 1024 * 1024;

const emptyEditForm = {
  DistributorName: "",
  AddressLine1: "",
  AddressLine2: "",
  City: "",
  State: "",
  Zip: "",
  Phone: "",
  Fax: "",
  EnableCommissionModule: true,
};

const boolValue = (value) => value === true || value === 1 || value === "1";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = moment(value);
  return date.isValid() ? date.format("MM/DD/YYYY") : "N/A";
};

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "N/A";
};

const DetailItem = ({ label, children }) => (
  <div className="distributor-detail-item">
    <Text variant="smallText" color="lightText">
      {label}
    </Text>
    <Text variant="smallText" color="darkText">
      {children}
    </Text>
  </div>
);

const StatusPill = ({ active, trueLabel = "Yes", falseLabel = "No" }) => (
  <span className={`distributor-status-pill ${active ? "success" : "failure"}`}>
    {active ? trueLabel : falseLabel}
  </span>
);

const ToggleField = ({ label, value, onChange }) => (
  <button
    className={`distributor-edit-toggle-card ${value ? "active" : ""}`}
    onClick={() => onChange(!value)}
    type="button"
  >
    <span>{label}</span>
    <span className="distributor-edit-toggle-track">
      <span className="distributor-edit-toggle-knob">{value ? "Y" : "N"}</span>
    </span>
  </button>
);

const EditDistributorModal = ({
  distributor,
  open,
  onClose,
  onSaved,
  isCreateMode,
}) => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(emptyEditForm);
  const [stateOptions, setStateOptions] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchStates = async () => {
      try {
        setStatesLoading(true);
        const apiServe = new APIService();
        const response = await apiServe.getDataFromBackend("getstatesmaster");
        const options = (response?.data?.data || [])
          .filter((state) => state?.Abbreviation)
          .map((state) => ({
            value: state.Abbreviation,
            label: state.Abbreviation,
          }));
        setStateOptions(options);
      } catch (error) {
        message.error("Unable to load states.");
        setStateOptions([]);
      } finally {
        setStatesLoading(false);
      }
    };
    fetchStates();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setFormValues({
      DistributorName: distributor?.DistributorName || "",
      AddressLine1: distributor?.AddressLine1 || "",
      AddressLine2: distributor?.AddressLine2 || "",
      City: distributor?.City || "",
      State: distributor?.State || "",
      Zip: distributor?.Zip || "",
      Phone: distributor?.Phone || "",
      Fax: distributor?.Fax || "",
      EnableCommissionModule: distributor?.EnableCommissionModule === undefined
        ? true
        : boolValue(distributor.EnableCommissionModule),
    });
  }, [distributor, open]);

  const setField = (field, value) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const validate = () => {
    const requiredFields = [
      ["DistributorName", "Distributor Name"],
      ["AddressLine1", "Address Line 1"],
      ["City", "City"],
      ["State", "State"],
      ["Zip", "ZIP"],
    ];
    const missing = requiredFields.find(([field]) => !String(formValues[field] || "").trim());
    if (missing) {
      message.error(`${missing[1]} is required.`);
      return false;
    }
    return true;
  };

  const handleClose = () => {
    if (isCreateMode) {
      navigate("/DistributorDashboard", { replace: true });
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const apiServe = new APIService();
      const payload = isCreateMode
        ? formValues
        : {
            ID: distributor.ID,
            ...formValues,
          };
      await apiServe.addDataToBackend(
        payload,
        isCreateMode ? "adddistributor" : "updatedistributor",
      );
      message.success(isCreateMode ? "Distributor added." : "Distributor updated.");
      onClose();
      if (isCreateMode) {
        navigate("/DistributorDashboard", { replace: true });
        return;
      }
      await onSaved?.();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to save distributor.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      centered
      footer={null}
      closable={false}
      width={600}
      onCancel={handleClose}
      className="distributor-edit-modal-shell"
      destroyOnClose
    >
      <div className="distributor-edit-modal">
        <div className="distributor-edit-modal-header">
          <Text variant="h1">{isCreateMode ? "Add Distributor" : "Edit Distributor"}</Text>
          <button className="distributor-icon-button" onClick={handleClose} type="button">
            <CloseOutlined />
          </button>
        </div>
        <div className="distributor-edit-divider" />

        <div className="distributor-edit-field full">
          <label>
            Distributor Name<span>*</span>
          </label>
          <Input
            value={formValues.DistributorName}
            onChange={(event) => setField("DistributorName", event.target.value)}
            placeholder="Distributor Name"
          />
        </div>

        <div className="distributor-edit-grid two">
          <div className="distributor-edit-field">
            <label>
              Address Line 1<span>*</span>
            </label>
            <Input
              value={formValues.AddressLine1}
              onChange={(event) => setField("AddressLine1", event.target.value)}
              placeholder="Address Line 1"
            />
          </div>
          <div className="distributor-edit-field">
            <label>
              Address Line 2
            </label>
            <Input
              value={formValues.AddressLine2}
              onChange={(event) => setField("AddressLine2", event.target.value)}
              placeholder="Code"
            />
          </div>
        </div>

        <div className="distributor-edit-grid city-state-zip">
          <div className="distributor-edit-field">
            <label>
              City<span>*</span>
            </label>
            <Input
              value={formValues.City}
              onChange={(event) => setField("City", event.target.value)}
              placeholder="City"
            />
          </div>
          <div className="distributor-edit-field">
            <label>
              State<span>*</span>
            </label>
            <Select
              showSearch
              loading={statesLoading}
              optionFilterProp="label"
              options={stateOptions}
              placeholder="State"
              value={formValues.State}
              onChange={(value) => setField("State", value)}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
            />
          </div>
          <div className="distributor-edit-field">
            <label>
              ZIP<span>*</span>
            </label>
            <Input
              value={formValues.Zip}
              onChange={(event) => setField("Zip", event.target.value)}
              placeholder="ZIP"
            />
          </div>
        </div>

        <div className="distributor-edit-grid two">
          <div className="distributor-edit-field">
            <label>Phone</label>
            <Input
              value={formValues.Phone}
              onChange={(event) => setField("Phone", event.target.value)}
              placeholder="Phone"
            />
          </div>
          <div className="distributor-edit-field">
            <label>Fax</label>
            <Input
              value={formValues.Fax}
              onChange={(event) => setField("Fax", event.target.value)}
              placeholder="Fax"
            />
          </div>
        </div>

        <div className="distributor-edit-section">
          <label>Settings</label>
          <div className="distributor-edit-toggle-row">
            <ToggleField
              label="Commissions"
              value={formValues.EnableCommissionModule}
              onChange={(value) => setField("EnableCommissionModule", value)}
            />
          </div>
        </div>

        <div className="distributor-edit-modal-footer">
          <button className="distributor-secondary-button" onClick={handleClose} type="button">
            Cancel
          </button>
          <button
            className="distributor-save-button"
            disabled={saving}
            onClick={handleSave}
            type="button"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const DistributorFilesSection = ({ distributorID, isAdmin, canAddFile }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchFiles = useCallback(async () => {
    if (!distributorID) return;
    try {
      setLoading(true);
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `getdistributorfiles?DistributorID=${distributorID}`,
      );
      setFiles(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      message.error("Unable to load distributor files.");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [distributorID]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const resetModal = () => {
    setModalOpen(false);
    setEditingFile(null);
    setDisplayName("");
    setSelectedFile(null);
    setUploadFileList([]);
  };

  const openAddModal = () => {
    setEditingFile(null);
    setDisplayName("");
    setSelectedFile(null);
    setUploadFileList([]);
    setModalOpen(true);
  };

  const openEditModal = (file) => {
    setEditingFile(file);
    setDisplayName(file.DisplayName || file.FileName || "");
    setSelectedFile(null);
    setUploadFileList([
      {
        uid: String(file.ID),
        name: file.FileName || file.DisplayName || "distributor-file",
        status: "done",
      },
    ]);
    setModalOpen(true);
  };

  const validateSelectedFile = (file, requireFile) => {
    if (!file && requireFile) return "Choose a file to upload.";
    if (!file) return "";
    if (!file.size) return "File cannot be empty.";
    if (file.size > MAX_DISTRIBUTOR_FILE_BYTES) {
      return "File must be 4 MB or smaller.";
    }
    return "";
  };

  const restoreExistingFileList = () => {
    if (!editingFile) {
      setUploadFileList([]);
      return;
    }

    setUploadFileList([
      {
        uid: String(editingFile.ID),
        name: editingFile.FileName || editingFile.DisplayName || "distributor-file",
        status: "done",
      },
    ]);
  };

  const handleUploadFile = ({ file, onSuccess, onError }) => {
    const error = validateSelectedFile(file, !editingFile);
    if (error) {
      message.error(error);
      setSelectedFile(null);
      restoreExistingFileList();
      onError?.(new Error(error));
      return;
    }

    setSelectedFile(file);
    setUploadFileList([{ uid: file.uid || file.name, name: file.name, status: "done" }]);
    if (file && !displayName.trim()) {
      setDisplayName(file.name);
    }
    setTimeout(() => {
      onSuccess?.("ok");
    }, 0);
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    fileList: uploadFileList,
    customRequest: handleUploadFile,
    onRemove: () => {
      if (editingFile && !selectedFile) {
        return false;
      }
      setSelectedFile(null);
      restoreExistingFileList();
      return true;
    },
  };

  const submitFile = async () => {
    const fileToUpload = selectedFile;
    const fileError = validateSelectedFile(fileToUpload, !editingFile);
    if (fileError) {
      message.destroy();
      message.error(fileError);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("DistributorID", distributorID);
      formData.append("DisplayName", displayName.trim() || fileToUpload?.name || editingFile?.DisplayName || "");
      if (editingFile) {
        formData.append("ID", editingFile.ID);
      }
      if (fileToUpload) {
        formData.append("file", fileToUpload);
      }

      const apiServe = new APIService();
      await apiServe.addFileDataToBackend(
        formData,
        editingFile ? "updatedistributorfile" : "adddistributorfile",
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      message.success(editingFile ? "File updated." : "File added.");
      resetModal();
      await fetchFiles();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to save file.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (file) => {
    if (!file?.BlobName) {
      message.error("File is missing storage metadata.");
      return;
    }
    try {
      const apiServe = new APIService();
      const sasUrl = await apiServe.getSasToken(file.BlobName);
      const exists = await apiServe.checkBlobExists(sasUrl);
      if (!exists) {
        message.error("File does not exist.");
        return;
      }

      const response = await axios.get(sasUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.FileName || file.DisplayName || "distributor-file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Unable to download file.");
    }
  };

  const deleteFile = async (file) => {
    const result = await Swal.fire({
      title: "Delete file?",
      text: "This will remove the file from this distributor.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.failureColor,
      cancelButtonColor: colors.strokeDark,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      const apiServe = new APIService();
      await apiServe.updateDataToBackend(
        { ID: file.ID, DistributorID: distributorID },
        "deletedistributorfile",
      );
      message.success("File deleted.");
      await fetchFiles();
    } catch (error) {
      message.error("Unable to delete file.");
    }
  };

  return (
    <section className="distributor-files-card">
      <div className="distributor-section-header">
        <Text variant="bodyText" color="darkText">
          Files
        </Text>
        {canAddFile && (
          <button className="distributor-small-primary-button" onClick={openAddModal} type="button">
            <PlusOutlined />
            Add File
          </button>
        )}
      </div>

      {loading ? (
        <div className="distributor-files-empty">
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        </div>
      ) : files.length === 0 ? (
        <div className="distributor-files-empty">
          <Text variant="smallText" color="lightText">
            No files attached.
          </Text>
        </div>
      ) : (
        <div className="distributor-files-grid">
          {files.map((file) => (
            <div className="distributor-file-tile" key={file.ID}>
              <div className="distributor-file-icon">
                <FileOutlined />
              </div>
              <div className="distributor-file-text">
                <Text variant="tinyText" color="lightText">
                  {formatDate(file.updatedAt || file.createdAt)}
                </Text>
                <Tooltip title={file.DisplayName || file.FileName}>
                  <span className="distributor-file-name">
                    {file.DisplayName || file.FileName}
                  </span>
                </Tooltip>
              </div>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "download",
                      label: "Download",
                      icon: <DownloadOutlined />,
                      onClick: () => downloadFile(file),
                    },
                    ...(isAdmin
                      ? [
                          {
                            key: "edit",
                            label: "Update",
                            icon: <EditOutlined />,
                            onClick: () => openEditModal(file),
                          },
                          {
                            key: "delete",
                            label: "Delete",
                            icon: <DeleteOutlined />,
                            danger: true,
                            onClick: () => deleteFile(file),
                          },
                        ]
                      : []),
                  ],
                }}
                trigger={["click"]}
              >
                <button className="distributor-file-menu" type="button">
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        title={editingFile ? "Update File" : "Add File"}
        centered
        onCancel={resetModal}
        onOk={submitFile}
        okText={submitting ? "Saving..." : "Save"}
        okButtonProps={{ disabled: submitting }}
        destroyOnClose
      >
        <div className="distributor-file-modal-body">
          <label className="distributor-file-modal-label">Display Name</label>
          <Input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Display Name"
          />
          <label className="distributor-file-modal-label">File</label>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Choose File</Button>
          </Upload>
          <Text variant="tinyText" color="lightText">
            Maximum file size is 4 MB.
          </Text>
        </div>
      </Modal>
    </section>
  );
};

const DistributorDetailsTab = ({
  distributorData,
  onDistributorUpdated,
  onDeactivateDistributor,
}) => {
  const { user } = useAuth();
  const params = useParams();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const isCreateMode = !params?.id;
  const isExistingDistributor = Boolean(params?.id && distributorData?.ID);

  useEffect(() => {
    if (isCreateMode) {
      setEditModalOpen(true);
    }
  }, [isCreateMode]);

  const userType = useMemo(() => {
    if (user?.Type === "Distributor") {
      return user?.IsManager ? "Manager" : "User";
    }
    return user?.Type || "";
  }, [user]);

  const isAdmin = user?.Type === "Admin";
  const canManageDistributor =
    user?.Type === "Admin" || user?.Type === "ASD" || user?.Type === "Authorizer";

  const reactivateDistributor = async () => {
    if (!distributorData?.ID) return;
    try {
      setReactivating(true);
      const apiServe = new APIService();
      await apiServe.updateDataToBackend(
        { DistributorID: distributorData.ID },
        "reactivatedistributor",
      );
      message.success("Distributor reactivated.");
      await onDistributorUpdated?.();
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.data ||
          "Unable to reactivate distributor.",
      );
    } finally {
      setReactivating(false);
    }
  };

  if (!distributorData) {
    return (
      <div className="distributor-details-empty">
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  return (
    <div className="distributor-details-page">
      <section className="distributor-summary-card">
        <div className="distributor-summary-grid">
          <div className="distributor-summary-column">
            <DetailItem label="Distributor Name">
              {displayValue(distributorData.DistributorName)}
            </DetailItem>
            <DetailItem label="Created On">
              {formatDate(distributorData.createdAt)}
            </DetailItem>
          </div>

          <div className="distributor-summary-column">
            <DetailItem label="Deactivated On">
              {formatDate(distributorData.DeactivatedOn)}
            </DetailItem>
            <DetailItem label="Reactivated On">
              {formatDate(distributorData.ReactivatedOn)}
            </DetailItem>
          </div>

          <div className="distributor-summary-column">
            <DetailItem label="Phone">{displayValue(distributorData.Phone)}</DetailItem>
            <DetailItem label="Fax">{displayValue(distributorData.Fax)}</DetailItem>
          </div>

          <div className="distributor-summary-column">
            <DetailItem label="Mailing Address">
              <span className="distributor-address-lines">
                <span>{displayValue(distributorData.AddressLine1)}</span>
                <span>{displayValue(distributorData.AddressLine2)}</span>
                <span>
                  {[
                    displayValue(distributorData.City),
                    displayValue(distributorData.State),
                    displayValue(distributorData.Zip),
                  ].filter((item) => item !== "N/A").join(", ") || "N/A"}
                </span>
              </span>
            </DetailItem>
          </div>

          <div className="distributor-summary-column compact">
            <DetailItem label="Commission Module">
              <StatusPill active={boolValue(distributorData.EnableCommissionModule)} />
            </DetailItem>
          </div>
        </div>

        {canManageDistributor && (
          <div className="distributor-summary-actions">
            {hasPermission(user, "manage_distributors.edit") && (
              <button
                className="distributor-action-button primary"
                onClick={() => setEditModalOpen(true)}
                type="button"
              >
                Edit
              </button>
            )}
            {hasPermission(user, "manage_distributors.delete") && (
              distributorData.IsActive ? (
                <button
                  className="distributor-action-button danger"
                  onClick={onDeactivateDistributor}
                  type="button"
                >
                  Deactivate
                </button>
              ) : (
                <button
                  className="distributor-action-button success"
                  disabled={reactivating}
                  onClick={reactivateDistributor}
                  type="button"
                >
                  {reactivating ? "..." : "Reactivate"}
                </button>
              )
            )}
          </div>
        )}
      </section>

      {isExistingDistributor && (
        <DistributorFilesSection
          distributorID={distributorData.ID}
          isAdmin={isAdmin}
          canAddFile={isAdmin && hasPermission(user, "manage_distributors.edit")}
        />
      )}

      {isExistingDistributor && (
        <div className={`distributor-related-tables ${userType === "User" ? "single" : ""}`}>
          <div className="distributor-related-table">
            <DistributorAccount distributorData={distributorData} />
          </div>
          {userType !== "User" && (
            <div className="distributor-related-table">
              <DistributorUser distributorData={distributorData} />
            </div>
          )}
        </div>
      )}

      <EditDistributorModal
        distributor={distributorData}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSaved={onDistributorUpdated}
        isCreateMode={isCreateMode}
      />
    </div>
  );
};

export default DistributorDetailsTab;
